import Vue from "vue";
import {
    normalizeActiveFilterValue,
    normalizeFilterStoredValue,
} from "@/utils/filterChoices";

const FILTER_STATE_KEYS = new Set([
    "limit",
    "offset",
    "ordering",
    "stock_tracking",
    "paginate",
    "format",
]);

export { normalizeActiveFilterValue };

/**
 * Stable signature for filtersConf arrays (avoids refresh loops on new references).
 */
export function buildFiltersConfSignature(filtersConf) {
    return JSON.stringify(
        (filtersConf || []).map((item) => {
            if (typeof item === "string") {
                return item;
            }
            if (!item || typeof item !== "object") {
                return item;
            }
            return {
                preset: item.preset || null,
                field: item.field || null,
                type: item.type || null,
                label: item.label || null,
                params: item.params || null,
                object_type: item.object_type || null,
            };
        }),
    );
}

function isConfiguredFilterKey(key, configuredFields) {
    if (configuredFields.has(key)) {
        return true;
    }
    const rangeMatch = key.match(/^(.+)_(min|max)$/);
    return !!(rangeMatch && configuredFields.has(rangeMatch[1]));
}

/**
 * Whether a filter configuration currently has an applied value.
 */
export function filterHasValue(filterconf, filters) {
    if (!filters || !filterconf?.field) {
        return false;
    }
    if (filterconf.type === "search") {
        const value = filters.search;
        return value !== null && value !== undefined && value !== "";
    }
    if (filterconf.type === "daterange" || filterconf.type === "numberrange") {
        const minField = `${filterconf.field}_min`;
        const maxField = `${filterconf.field}_max`;
        if (filters[minField] || filters[maxField]) {
            return true;
        }
        if (filterconf.type === "daterange") {
            const presetField = `${filterconf.field}_preset`;
            return (
                filters[presetField] !== null &&
                filters[presetField] !== undefined &&
                filters[presetField] !== ""
            );
        }
        return false;
    }
    if (filterconf.type === "slider") {
        const n = Number(filters[filterconf.field]);
        return Number.isFinite(n) && n > 0;
    }
    const value = normalizeActiveFilterValue(
        filters[filterconf.field],
        filterconf,
    );
    if (value === null || value === undefined || value === "") {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (filterconf.type === "boolean") {
        // Boolean filters may apply a custom payload (e.g. customer/entity id)
        // instead of `true`. Treat that payload as an active value.
        if (filterconf.value !== undefined && filterconf.value !== null) {
            return (
                value === filterconf.value ||
                String(value) === String(filterconf.value)
            );
        }
        if (value === true) {
            return true;
        }
        return Boolean(filterconf.force_value) && value === false;
    }
    return true;
}

/**
 * Return filter keys that have a value but no matching config in fullFiltersConf.
 */
export function findActiveFilterKeysMissingFromConf(fullFiltersConf, filters) {
    if (!filters) {
        return [];
    }
    const configuredFields = new Set(
        (fullFiltersConf || []).map((conf) => conf.field).filter(Boolean),
    );
    return Object.keys(filters).filter((key) => {
        if (
            FILTER_STATE_KEYS.has(key) ||
            key.startsWith("metadata__") ||
            key.startsWith("productmetadata__") ||
            key === "products"
        ) {
            return false;
        }
        if (isConfiguredFilterKey(key, configuredFields)) {
            return false;
        }
        const raw = filters[key];
        if (raw === null || raw === undefined || raw === "") {
            return false;
        }
        if (Array.isArray(raw) && raw.length === 0) {
            return false;
        }
        if (raw === false) {
            return false;
        }
        return true;
    });
}

/**
 * Resolve display text for a choice value.
 * Pass `vm` (a Vue component) to get a translated, label-prefixed text when
 * more than 2 selected values are collapsed into a count.
 */
export function resolveFilterValueLabel(filterconf, value, choices, vm = null) {
    const normalizedValue = normalizeActiveFilterValue(value, filterconf);
    if (normalizedValue === null || normalizedValue === undefined) {
        return "";
    }
    const flatChoices = flattenChoices(choices);
    if (Array.isArray(normalizedValue)) {
        const labels = normalizedValue
            .map(
                (v) =>
                    flatChoices.find((c) => valuesMatch(c.value, v))?.text ||
                    String(v),
            )
            .filter(Boolean);
        if (labels.length === 0) {
            return "";
        }
        if (labels.length <= 2) {
            return labels.join(", ");
        }
        return formatCollapsedValuesLabel(filterconf, labels.length, vm);
    }
    const match = flatChoices.find((c) => valuesMatch(c.value, normalizedValue));
    if (match?.text) {
        return match.text;
    }
    if (typeof normalizedValue === "boolean") {
        return normalizedValue ? "Yes" : "No";
    }
    return String(normalizedValue);
}

/**
 * Text for a multi-select filter whose selected values are collapsed (> 2).
 * Prefixed with the filter label so the pill stays understandable when the
 * individual values are not displayed (e.g. "Status: 3 values").
 */
function formatCollapsedValuesLabel(filterconf, count, vm) {
    const key = "collapsedFilterValues";
    let template = "{count} values";
    if (typeof vm?.$translate === "function") {
        const translated = vm.$translate(key);
        if (translated && translated !== key) {
            template = translated;
        }
    }
    const countLabel = template.replace("{count}", String(count));
    const label = filterconf?.label;
    return label ? `${label}: ${countLabel}` : countLabel;
}

function prefixFilterBadgeValue(label, valueLabel) {
    if (!valueLabel) {
        return label || "";
    }
    if (!label) {
        return valueLabel;
    }
    const prefix = `${label}: `;
    if (valueLabel.startsWith(prefix)) {
        return valueLabel;
    }
    return `${label}: ${valueLabel}`;
}

function valuesMatch(choiceValue, filterValue) {
    return String(choiceValue) === String(filterValue);
}

function flattenChoices(choices) {
    if (!choices?.length) {
        return [];
    }
    const result = [];
    choices.forEach((item) => {
        if (item.header || item.divider) {
            return;
        }
        if (item.value !== undefined) {
            result.push(item);
        }
    });
    return result;
}

/**
 * Format a human-readable badge label for an active filter.
 */
export function formatFilterBadgeLabel(
    filterconf,
    filters,
    choices,
    utils,
    valueLabelOverride = null,
    vm = null,
) {
    if (!filterHasValue(filterconf, filters)) {
        return null;
    }
    const label = filterconf.label || filterconf.field;

    if (filterconf.type === "search") {
        return String(filters.search);
    }

    if (filterconf.type === "daterange" || filterconf.type === "numberrange") {
        const minField = `${filterconf.field}_min`;
        const maxField = `${filterconf.field}_max`;
        const min = filters[minField];
        const max = filters[maxField];
        if (filterconf.type === "daterange" && utils?.formatDateRangeLabel) {
            if (min && max) {
                return utils.formatDateRangeLabel(min, max);
            }
            if (min || max) {
                return utils.formatDateRangeLabel(min || max, max || min);
            }
        }
        if (filterconf.type === "numberrange" && utils?.price) {
            if (min != null && max != null) {
                return `${utils.price(min)} – ${utils.price(max)}`;
            }
            if (min != null) {
                return `≥ ${utils.price(min)}`;
            }
            if (max != null) {
                return `≤ ${utils.price(max)}`;
            }
        }
        if (min && max) {
            return `${min} – ${max}`;
        }
        if (min) {
            return `≥ ${min}`;
        }
        if (max) {
            return `≤ ${max}`;
        }
    }

    if (filterconf.type === "boolean") {
        return label;
    }

    if (filterconf.type === "slider") {
        const n = Number(filters[filterconf.field]);
        if (!Number.isFinite(n) || n <= 0) {
            return null;
        }
        const unit = filterconf.unit || "";
        return prefixFilterBadgeValue(label, `≥ ${Math.round(n)}${unit}`);
    }

    const value = filters[filterconf.field];
    const normalizedValue = normalizeActiveFilterValue(value, filterconf);
    const valueLabel =
        valueLabelOverride ||
        resolveFilterValueLabel(filterconf, value, choices, vm);
    if (valueLabel) {
        if (
            filterconf.type !== "search" &&
            filterconf.type !== "boolean" &&
            filterconf.type !== "daterange" &&
            filterconf.type !== "numberrange"
        ) {
            return prefixFilterBadgeValue(label, valueLabel);
        }
        return valueLabel;
    }
    if (
        normalizedValue !== null &&
        normalizedValue !== undefined &&
        normalizedValue !== ""
    ) {
        return String(normalizedValue);
    }
    return label;
}

/**
 * Count non-pagination active filters (excluding search optionally).
 */
export function countActiveFilters(fullFiltersConf, filters, { excludeSearch = true } = {}) {
    if (!filters) {
        return 0;
    }
    let count = (fullFiltersConf || []).filter((conf) => {
        if (excludeSearch && conf.type === "search") {
            return false;
        }
        return filterHasValue(conf, filters);
    }).length;
    if (Array.isArray(filters.products) && filters.products.length) {
        count += filters.products.length;
    }
    return count;
}

/**
 * Pick quick-access filter configs shown as pills when the panel is open.
 */
export function getQuickFilterConfigs(fullFiltersConf, { pinnedOnly = false, maxCount = 3 } = {}) {
    const candidates = (fullFiltersConf || []).filter(
        (conf) => conf.type !== "search" && conf.type !== "dynamicfilters",
    );
    const pinned = candidates.filter((conf) => conf.pinned || conf.force_display);
    if (pinned.length) {
        return pinned;
    }
    return candidates.slice(0, maxCount);
}

/**
 * Filters available in the "add filter" menu.
 */
export function getAddableFilterConfigs(fullFiltersConf, filters, visibleFields) {
    const visible = new Set(visibleFields || []);
    return (fullFiltersConf || []).filter((conf) => {
        if (conf.type === "search" || conf.type === "dynamicfilters") {
            return false;
        }
        if (visible.has(conf.field)) {
            return false;
        }
        return true;
    });
}

/**
 * Format date using moment if available.
 */
export function formatHumandate(value) {
    if (!value) {
        return "";
    }
    if (Vue.moment) {
        return Vue.moment(value).format("LL");
    }
    return String(value);
}

export function isBooleanFilter(filterconf) {
    return filterconf?.type === "boolean";
}

/** Value applied when a boolean filter is toggled on from the toolbar. */
export function booleanFilterApplyValue(filterconf) {
    if (filterconf?.value !== undefined && filterconf?.value !== null) {
        return filterconf.value;
    }
    return true;
}
